/**
 * PO Lines Modal Components
 * Put on Hold and Release Hold modals
 */
import { X } from 'lucide-react';
import { Button } from '../../../ui/button';
import { PurchaseOrderLine } from './types';
import { HoldNextActionChoice, type HoldNextAction } from '../../HoldNextActionChoice';

interface PutOnHoldModalProps {
  isOpen: boolean;
  line: PurchaseOrderLine | null;
  holdReason: string;
  onHoldReasonChange: (reason: string) => void;
  /** G7: whose move it is, and whether the placer keeps a Release control. */
  holdNextAction: HoldNextAction;
  onHoldNextActionChange: (next: HoldNextAction) => void;
  /** Absent when the placer is neither side of the deal — see HoldReasonModal. */
  placerSide?: 'customer' | 'supplier';
  onClose: () => void;
  onSubmit: () => void;
}

export function PutOnHoldModal({
  isOpen,
  line,
  holdReason,
  onHoldReasonChange,
  holdNextAction,
  onHoldNextActionChange,
  placerSide,
  onClose,
  onSubmit,
}: PutOnHoldModalProps) {
  if (!isOpen || !line) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Put Line On Hold</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground mb-2">
              Line {line.line_number} - {line.part_number}
            </p>
            <p className="text-sm">{line.part_description}</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Reason for hold <span className="text-red-500">*</span>
            </label>
            <textarea
              value={holdReason}
              onChange={(e) => onHoldReasonChange(e.target.value)}
              className="w-full p-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary min-h-[100px]"
              placeholder="Enter reason for putting this line on hold..."
            />
          </div>
          {placerSide ? (
            <div className="mt-4">
              <HoldNextActionChoice
                placerSide={placerSide}
                value={holdNextAction}
                onChange={onHoldNextActionChange}
              />
            </div>
          ) : (
            <p className="mt-4 text-xs text-muted-foreground">
              This hold stays with you — neither the customer nor the supplier can
              take it off.
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 p-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            className="bg-orange-600 hover:bg-orange-700"
          >
            Put On Hold
          </Button>
        </div>
      </div>
    </div>
  );
}

interface ReleaseHoldModalProps {
  isOpen: boolean;
  line: PurchaseOrderLine | null;
  onClose: () => void;
  onSubmit: () => void;
}

export function ReleaseHoldModal({
  isOpen,
  line,
  onClose,
  onSubmit,
}: ReleaseHoldModalProps) {
  if (!isOpen || !line) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Release from Hold</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground mb-2">
              Line {line.line_number} - {line.part_number}
            </p>
            <p className="text-sm">{line.part_description}</p>
          </div>
          {line.on_hold_reason_details?.description && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm font-medium text-orange-900 mb-1">Current hold reason:</p>
              <p className="text-sm text-orange-800">
                {line.on_hold_reason_details.description}
              </p>
            </div>
          )}
          <p className="text-sm text-muted-foreground">
            Are you sure you want to release this line from hold?
          </p>
        </div>
        <div className="flex items-center justify-end gap-3 p-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            className="bg-green-600 hover:bg-green-700"
          >
            Release Hold
          </Button>
        </div>
      </div>
    </div>
  );
}
